import React, { useMemo } from "react";
import { Container } from "reactstrap";
import { GlobalImage } from "config/image";
import { formatDate_DD_MMM_YYYY } from "utils/formatDates";
import "../assets/scss/components/_viewDetail.scss";
import { ItemData } from "types/item.api";
import { CompanyPayload } from "types/auth.api";
import { QuoteInvoiceSettingsApiArgs } from "types/setting.api";
import CustomButton from "./Custom/CustomButton";
import ItemsTable from "Components/Common/PdfPreview/ItemList";
import { ViewInvoicePayload, ViewQuotePayload } from "types/email.api";
import SmartImage from "./Common/SmartImage";

interface Originator {
  name: string;
}

interface FinancialSummaryArgs {
  sub_total: number;
  tax: number;
  discount: number;
  grand_total: number;
  total_cost: number;
}

interface DocumentViewLayoutProps {
  title: string;

  originator: Originator;
  company: CompanyPayload | null;

  documentNumberLabel: string;
  documentNumber: string;

  documentDateLabel: string;
  documentDate: string;
  dueDate: string;

  items: ItemData[];
  columns: any[];

  financialSummary: FinancialSummaryArgs | null;
  quoteInvoiceSettings?: QuoteInvoiceSettingsApiArgs;
  handleAction?: (status: "approved" | "rejected") => void;
  btnName: string;
  showButtons?: boolean;
  quoteDetails?: ViewQuotePayload;
  invoiceDetails?: ViewInvoicePayload;
  onDownload?: () => void;
  isDownloading?: boolean;
  showPayDepositButton?: boolean;
  onPayDeposit?: () => void;
  payAmount?: number;
  paymentStatus: string;
  isPayLoading?: boolean;
}

const DocumentViewLayout: React.FC<DocumentViewLayoutProps> = ({
  title,
  // originator,
  company,
  documentNumberLabel,
  documentNumber,
  documentDateLabel,
  documentDate,
  dueDate,
  // items,
  // columns,
  financialSummary,
  handleAction,
  quoteInvoiceSettings,
  btnName,
  showButtons = false,
  quoteDetails,
  invoiceDetails,
  onDownload,
  isDownloading = false,
  showPayDepositButton = false,
  onPayDeposit,
  payAmount,
  paymentStatus,
  isPayLoading,
}) => {
  const type: "quote" | "invoice" = quoteDetails ? "quote" : "invoice";

  const rawGrandTotal = financialSummary?.grand_total ?? 0;
  const depositApplied =
    type === "invoice" && invoiceDetails?.deposit_payment?.status === "received"
      ? Math.min(invoiceDetails?.deposit_payment?.amount ?? 0, rawGrandTotal)
      : 0;
  const grandTotalAfterDeposit = Math.max(rawGrandTotal - depositApplied, 0);
  const tableData =
    type === "quote"
      ? quoteDetails?.quote.items
      : invoiceDetails?.invoice.items;

  const selectedOption =
    type === "quote"
      ? quoteDetails?.quote.categorised
      : invoiceDetails?.invoice.categorised;

  const { categoryViewData, subcategoryViewData, allViewData } = useMemo(() => {
    const result = {
      categoryViewData: [] as {
        category: string;
        itemsCount: number;
        total: number;
      }[],
      subcategoryViewData: [] as {
        subcategory: string;
        itemsCount: number;
        total: number;
      }[],
      allViewData: [] as {
        categoryId: string | number;
        category: string;
        rows: any[];
      }[],
    };

    if (!Array.isArray(tableData)) return result;

    const categoryMap = new Map<
      string | number,
      {
        categoryId: string | number;
        category: string;
        subMap: Map<
          string | number,
          {
            subcategoryId: string | number;
            subcategory: string;
            itemMap: Map<
              string,
              {
                name: string;
                qty: number;
                price: number;
                total: number;
                unit: string;
              }
            >;
          }
        >;
      }
    >();

    tableData.forEach((item) => {
      const categoryId = item.category_id ?? "na";
      const categoryName = item.category_name ?? "-";

      const subId = item.subcategory_id ?? "na";
      const subName = item.subcategory_name ?? "-";

      if (!categoryMap.has(categoryId)) {
        categoryMap.set(categoryId, {
          categoryId,
          category: categoryName,
          subMap: new Map(),
        });
      }

      const categoryData = categoryMap.get(categoryId)!;

      if (!categoryData.subMap.has(subId)) {
        categoryData.subMap.set(subId, {
          subcategoryId: subId,
          subcategory: subName,
          itemMap: new Map(),
        });
      }

      const subData = categoryData.subMap.get(subId)!;

      const itemKey = item.name?.toLowerCase() ?? "unknown-item";
      const qty = Number(item.quantity ?? 0);
      const price = Number(item.price ?? 0);

      if (!subData.itemMap.has(itemKey)) {
        subData.itemMap.set(itemKey, {
          name: item.name,
          qty,
          price,
          total: qty * price,
          unit: item.unit,
        });
      } else {
        const exist = subData.itemMap.get(itemKey)!;
        exist.qty += qty;
        exist.total += qty * price;
      }
    });

    const categoryArray = Array.from(categoryMap.values());

    // CATEGORIES summary
    let catTemp = categoryArray.map((cat) => {
      const subItems = Array.from(cat.subMap.values()).flatMap((sub) =>
        Array.from(sub.itemMap.values()),
      );
      return {
        category: cat.category,
        itemsCount: subItems.reduce((s, i) => s + i.qty, 0),
        total: subItems.reduce((s, i) => s + i.total, 0),
      };
    });

    // Merge - Categories
    const catNA = catTemp.filter((c) => c.category === "-");
    const catValid = catTemp.filter((c) => c.category !== "-");

    if (catNA.length > 0) {
      const merged = catNA.reduce(
        (acc, c) => {
          acc.itemsCount += c.itemsCount;
          acc.total += c.total;
          return acc;
        },
        { category: "-", itemsCount: 0, total: 0 },
      );
      result.categoryViewData = [...catValid, merged];
    } else {
      result.categoryViewData = catValid;
    }

    // SUBCATEGORY summary
    let subTemp = categoryArray.flatMap((cat) =>
      Array.from(cat.subMap.values()).map((sub) => {
        const items = Array.from(sub.itemMap.values());
        return {
          subcategory: sub.subcategory,
          itemsCount: items.reduce((s, i) => s + i.qty, 0),
          total: items.reduce((s, i) => s + i.total, 0),
        };
      }),
    );

    // Merge - Subcategories
    const subNA = subTemp.filter((s) => s.subcategory === "-");
    const subValid = subTemp.filter((s) => s.subcategory !== "-");

    if (subNA.length > 0) {
      const mergedSub = subNA.reduce(
        (acc, s) => {
          acc.itemsCount += s.itemsCount;
          acc.total += s.total;
          return acc;
        },
        { subcategory: "-", itemsCount: 0, total: 0 },
      );
      result.subcategoryViewData = [...subValid, mergedSub];
    } else {
      result.subcategoryViewData = subValid;
    }

    // ALL view (Category → Subcategory → Items)
    result.allViewData = categoryArray.map((cat) => ({
      categoryId: cat.categoryId,
      category: cat.category,
      rows: Array.from(cat.subMap.values()).flatMap((sub) =>
        Array.from(sub.itemMap.values()).map((i) => ({
          subcategory: sub.subcategory,
          item: i.name,
          qty: i.qty,
          unitPrice: i.price,
          total: i.total,
          unit: i.unit,
        })),
      ),
    }));

    return result;
  }, [tableData]);

  const mainTableData = useMemo(() => {
    switch (selectedOption) {
      case "by-category":
        return categoryViewData;
      case "by-subcategory":
        return subcategoryViewData;
      case "by-item":
      default:
        return tableData;
    }
  }, [selectedOption, categoryViewData, subcategoryViewData, tableData]);

  const newColumns = useMemo(() => {
    switch (selectedOption) {
      case "by-item":
        return [
          {
            header: "Item",
            accessorKey: "name",
            enableColumnFilter: false,
            enableSorting: false,
            cell: (cell: any) => (
              <span className="fw-normal text-black2D fs-14 font-primary ms-1">
                {cell.getValue()}
              </span>
            ),
          },
          {
            header: "Unit",
            accessorKey: "unit",
            enableColumnFilter: false,
            enableSorting: false,
            cell: (cell: any) => (
              <span className="fw-normal text-black2D fs-14 font-primary ms-1">
                {cell.getValue()}
              </span>
            ),
          },
          {
            header: "Qty",
            accessorKey: "quantity",
            enableColumnFilter: false,
            enableSorting: false,
            cell: (cell: any) => (
              <span className="fw-normal text-black2D fs-14 font-primary ms-1">
                {cell.getValue()}
              </span>
            ),
          },
          {
            header: "Unit Price",
            accessorKey: "price",
            enableColumnFilter: false,
            enableSorting: false,
            cell: (cell: any) => {
              const value = Number(cell.getValue() ?? 0);
              return (
                <span className="fw-normal text-black2D fs-14 font-primary ms-1">
                  £{value.toFixed(2)}
                </span>
              );
            },
          },
          {
            header: "Total",
            accessorKey: "total",
            enableColumnFilter: false,
            enableSorting: false,
            cell: (cell: any) => {
              const row = cell.row.original as ItemData;
              const qty = Number(row.quantity ?? 0);
              const price = Number(row.price ?? 0);
              const total = qty * price;

              return (
                <span className="fw-normal text-black2D fs-14 font-primary ms-1">
                  £{total.toFixed(2)}
                </span>
              );
            },
          },
        ];

      case "by-category":
        return [
          {
            header: "Category",
            accessorKey: "category",
            enableColumnFilter: false,
            enableSorting: false,
            cell: (cell: any) => (
              <span className="fw-normal text-black2D fs-14 font-primary ms-1">
                {cell.getValue() ? cell.getValue() : "-"}
              </span>
            ),
          },
          {
            header: "Items",
            accessorKey: "itemsCount",
            enableColumnFilter: false,
            enableSorting: false,
            cell: (cell: any) => (
              <span className="fw-normal text-black2D fs-14 font-primary ms-1">
                {cell.getValue()}
              </span>
            ),
          },
          {
            header: "Total",
            accessorKey: "total",
            enableColumnFilter: false,
            enableSorting: false,
            cell: (cell: any) => {
              const value = Number(cell.getValue() ?? 0);
              return (
                <span className="fw-normal text-black2D fs-14 font-primary ms-1">
                  £{value.toFixed(2)}
                </span>
              );
            },
          },
        ];

      case "by-subcategory":
        return [
          {
            header: "Subcategory",
            accessorKey: "subcategory",
            enableColumnFilter: false,
            enableSorting: false,
            cell: (cell: any) => (
              <span className="fw-normal text-black2D fs-14 font-primary ms-1">
                {cell.getValue() ? cell.getValue() : "-"}
              </span>
            ),
          },
          {
            header: "Items",
            accessorKey: "itemsCount",
            enableColumnFilter: false,
            enableSorting: false,
            cell: (cell: any) => (
              <span className="fw-normal text-black2D fs-14 font-primary ms-1">
                {cell.getValue()}
              </span>
            ),
          },
          {
            header: "Total",
            accessorKey: "total",
            enableColumnFilter: false,
            enableSorting: false,
            cell: (cell: any) => {
              const value = Number(cell.getValue() ?? 0);
              return (
                <span className="fw-normal text-black2D fs-14 font-primary ms-1">
                  £{value.toFixed(2)}
                </span>
              );
            },
          },
        ];

      case "by-category-subcategory-item":
      default:
        return [
          {
            header: "Subcategory",
            accessorKey: "subcategory",
            enableColumnFilter: false,
            enableSorting: false,
            cell: (cell: any) => (
              <span className="fw-normal text-black2D fs-14 font-primary ms-1">
                {cell.getValue() ? cell.getValue() : "-"}
              </span>
            ),
          },
          {
            header: "Item",
            accessorKey: "item",
            enableColumnFilter: false,
            enableSorting: false,
            cell: (cell: any) => (
              <span className="fw-normal text-black2D fs-14 font-primary ms-1">
                {cell.getValue()}
              </span>
            ),
          },
          {
            header: "Unit",
            accessorKey: "unit",
            enableColumnFilter: false,
            enableSorting: false,
            cell: (cell: any) => (
              <span className="fw-normal text-black2D fs-14 font-primary ms-1">
                {cell.getValue()}
              </span>
            ),
          },
          {
            header: "Qty",
            accessorKey: "qty",
            enableColumnFilter: false,
            enableSorting: false,
            cell: (cell: any) => (
              <span className="fw-normal text-black2D fs-14 font-primary ms-1">
                {cell.getValue()}
              </span>
            ),
          },

          {
            header: "Unit Price",
            accessorKey: "unitPrice",
            enableColumnFilter: false,
            enableSorting: false,
            cell: (cell: any) => {
              const value = Number(cell.getValue() ?? 0);
              return (
                <span className="fw-normal text-black2D fs-14 font-primary ms-1">
                  £{value.toFixed(2)}
                </span>
              );
            },
          },
          {
            header: "Total",
            accessorKey: "total",
            enableColumnFilter: false,
            enableSorting: false,
            cell: (cell: any) => {
              const value = Number(cell.getValue() ?? 0);
              return (
                <span className="fw-normal text-black2D fs-14 font-primary ms-1">
                  £{value.toFixed(2)}
                </span>
              );
            },
          },
        ];
    }
  }, [selectedOption]);

  return (
    <div className="quote-page">
      {/* HEADER */}
      <div className="quote-header">
        <div className="quote-header-svg-wrapper">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 1920 224"
            preserveAspectRatio="none"
            className="quote-header-svg"
          >
            <path
              d="M0 0H1920V111C1920 111 1337.49 224 960 224C582.508 224 0 111 0 111V0Z"
              fill="url(#paint0_linear_4060_19823)"
            />
            <defs>
              <linearGradient
                id="paint0_linear_4060_19823"
                x1="1920"
                y1="0"
                x2="1873.68"
                y2="427.16"
                gradientUnits="userSpaceOnUse"
              >
                <stop stopColor="#F2EEEC" />
                <stop offset="1" stopColor="#E8E8F2" />
              </linearGradient>
            </defs>
          </svg>

          <div className="quote-header-title">
            <h1 className="fs-32 fw-semibold text-black2D font-primary">
              {title}
            </h1>
          </div>
        </div>
      </div>

      {/* BODY */}
      <Container className="quote-body">
        <div className="d-flex justify-content-between align-items-start">
          <div className="quote-meta">
            {/* <h1 className="fs-16 text-black2D fw-semibold font-primary mb-2 clamp-4">
              {originator.name}
            </h1> */}

            <div className="d-flex gap-2 align-items-center mb-2">
              {company?.logo?.length && (
                <SmartImage src={company.logo} alt="logo" maxHeight={40} />
              )}

              <span className="fs-16 text-black2D fw-normal font-primary clamp-4">
                {company?.is_company_name_show && company?.name}
              </span>
            </div>

            {/* <div className="fs-16 text-black2D fw-normal font-primary clamp-4">
              {company?.name}
            </div> */}

            {company?.billing_details?.phone &&
              (quoteDetails?.quote?.is_company_phone_number_show ||
                invoiceDetails?.invoice?.is_company_phone_number_show) && (
                <div className="fs-16 text-black2D fw-normal font-primary clamp-4">
                  (+44) {company.billing_details.phone}
                </div>
              )}

            <div className="fs-16 text-black2D fw-normal font-primary clamp-4">
              {company?.address?.address}, {company?.address?.city},{" "}
              {company?.address?.country}, {company?.address?.postcode}
            </div>

            <div className="invoice-grid">
              <div>{documentNumberLabel}</div>
              <div>{documentNumber}</div>

              <div>{documentDateLabel}</div>
              <div>{formatDate_DD_MMM_YYYY(documentDate, true)}</div>

              <div>Amount Due</div>
              <div>£{grandTotalAfterDeposit.toFixed(2)}</div>

              <div>Due Date</div>
              <div>{formatDate_DD_MMM_YYYY(dueDate, true)}</div>
            </div>
          </div>

          {onDownload && (
            <CustomButton
              type="button"
              className="bg-primary text-white br-7 px-3"
              text={isDownloading ? "Downloading..." : `Download ${btnName}`}
              onClick={onDownload}
              disabled={isDownloading}
            />
          )}
        </div>

        {/* ITEMS TABLE */}
        <div>
          {((mainTableData && mainTableData.length > 0) ||
            allViewData.length > 0) && (
              <>
                {selectedOption === "by-category-subcategory-item" ? (
                  <>
                    {allViewData.map((cat) => (
                      <div key={cat.categoryId} className="mt-4">
                        {/* Category heading like screenshot (MATERIAL, SERVICES, etc.) */}
                        <div className="fw-semibold text-uppercase fs-14 ">
                          {cat.category}
                        </div>

                        <ItemsTable columns={newColumns} data={cat.rows} />
                      </div>
                    ))}
                  </>
                ) : (
                  <div className="mt-3">
                    <ItemsTable columns={newColumns} data={mainTableData} />
                  </div>
                )}
              </>
            )}
        </div>

        {/* FINANCIAL SUMMARY */}
        <div className="invoice-grid">
          <div>Sub-total</div>
          <div>£{financialSummary?.sub_total.toFixed(2)}</div>

          <div>Tax</div>
          <div>£{financialSummary?.tax.toFixed(2)}</div>

          <div>Discount</div>
          <div>£{financialSummary?.discount.toFixed(2)}</div>
          {type === "quote" && quoteDetails?.quote.deposit_required && (
            <>
              <div>Deposit Amount</div>
              <div
                className={
                  quoteDetails?.deposit_payment?.status === "pending"
                    ? "text-warning"
                    : quoteDetails?.deposit_payment?.status === "failed"
                      ? "text-danger"
                      : "text-success"
                }
              >
                £{quoteDetails?.quote.deposit_amount.toFixed(2)}
              </div>
            </>
          )}

          {depositApplied > 0 && (
            <>
              <div>Deposit Applied</div>
              <div>£{depositApplied.toFixed(2)}</div>
            </>
          )}

          <div className="total-label">Grand Total</div>
          <div className="total-value">
            £{grandTotalAfterDeposit.toFixed(2)}
          </div>
        </div>

        <div className="quote-terms">
          <strong>Bank & Payment Details</strong>
          <div className="invoice-grid">
            <div>Bank:</div>
            <div>
              {type === "quote"
                ? quoteDetails?.company?.billing_details?.bank_name
                : invoiceDetails?.company?.billing_details?.bank_name}
            </div>

            <div>Account Name:</div>
            <div>
              {type === "quote"
                ? quoteDetails?.company?.billing_details?.name
                : invoiceDetails?.company?.billing_details?.name}
            </div>

            <div>Account Number:</div>
            <div>
              {type === "quote"
                ? quoteDetails?.company?.billing_details?.account_number
                : invoiceDetails?.company?.billing_details?.account_number}
            </div>

            <div>Sort Code:</div>
            <div>
              {type === "quote"
                ? quoteDetails?.company?.billing_details?.sort_code
                : invoiceDetails?.company?.billing_details?.sort_code}
            </div>
          </div>
        </div>

        {/* TERMS */}
        <div className="quote-terms">
          <strong className="fs-16 text-black2d fw-semibold">
            Terms & Conditions
          </strong>
          <p className="clamp-4 font-primary fs-16 text-black2d fw-normal">
            {quoteInvoiceSettings?.terms_and_conditions}
          </p>
        </div>

        {payAmount != null && payAmount > 0 && (
          <div
            className="mt-3 mb-3 p-3 br-8"
            style={{ backgroundColor: "#F8F8FC" }}
          >
            <p className="mb-1 fs-14 font-primary d-flex align-items-center">
              {paymentStatus === "received" && (
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                  className="me-2 flex-shrink-0"
                >
                  <circle cx="12" cy="12" r="12" fill="#2E9D73" />
                  <path
                    d="M7 12.5L10.5 16L17 9.5"
                    stroke="white"
                    strokeWidth="2.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              )}
              <strong
                className={`fs-16 fw-semibold ${paymentStatus === "received" ? "text-green" : "text-black2d"
                  }`}
              >
                {type === "invoice"
                  ? invoiceDetails?.invoice?.status === "paid"
                    ? "Invoice Paid"
                    : "Amount Due:"
                  : paymentStatus === "received"
                    ? "Deposit Paid:"
                    : showPayDepositButton
                      ? "Deposit Pending:"
                      : "Deposit (optional):"}
              </strong>{" "}
              {invoiceDetails?.invoice?.status !== "paid" && (
                <span className="text-green fw-semibold fs-16 ms-1">
                  £
                  {Number(payAmount).toLocaleString("en-GB", {
                    minimumFractionDigits: 0,
                    maximumFractionDigits: 2,
                  })}
                </span>
              )}
            </p>
            <p className="mb-0 font-primary fs-16 text-black2d fw-normal">
              {type === "invoice"
                ? invoiceDetails?.invoice?.status === "paid"
                  ? "Your payment has been received and this invoice has been marked as paid. A confirmation has been sent to your email."
                  : "Your invoice is ready for payment. Please pay the amount due to complete your balance."
                : invoiceDetails?.invoice?.status === "paid"
                  ? "Your deposit has been received. This confirms your booking for the job. A confirmation has been sent to your email."
                  :  quoteDetails?.deposit_payment === null ? "Please pay the required deposit using the agreed offline payment method to proceed." : showPayDepositButton
                    ? "You’ve accepted this quote. You can pay a deposit now to confirm your booking."
                    : "You can pay a deposit after accepting the quote to confirm your booking."}
              {btnName.toLowerCase() !== "invoice" && (
                <>
                  <br />
                  Any deposit paid will be deducted from your final invoice.
                </>
              )}
            </p>
          </div>
        )}

        {/* SIGNATURE */}
        <div className="d-flex flex-column align-items-start mt-3 mb-3">
          <div className="mt-3">
            <img
              src={
                quoteInvoiceSettings?.signature || GlobalImage.dummySignature
              }
              alt="signature"
              width={75}
              className="mb-1"
            />
          </div>

          <span className="text-black2D font-primary fs-16 fw-normal">
            Your Signature
          </span>
        </div>

        {/* ACTIONS (Quote / Invoice specific) */}
        {showButtons && (
          <div className="quote-actions">
            <CustomButton
              type="button"
              className="bg-primary text-white br-7 px-3"
              text={`Accept ${btnName}`}
              onClick={() => handleAction?.("approved")}
            />
            <CustomButton
              type="button"
              className="bg-redLight br-7 px-3 ms-2"
              text={`Reject ${btnName}`}
              textClassName="text-red fs-14 fw-normal"
              onClick={() => handleAction?.("rejected")}
            />
          </div>
        )}

        {!showButtons && showPayDepositButton && (
          <div className="quote-actions">
            <CustomButton
              type="button"
              className="bg-primary text-white br-7 px-3"
              text="Pay Now"
              onClick={onPayDeposit}
              loading={isPayLoading}
            />
          </div>
        )}
      </Container>
    </div>
  );
};

export default DocumentViewLayout;
